import { useEffect, useMemo } from 'react';
import { useAsync } from 'react-use';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import GroupIcon from '@material-ui/icons/Group';
import PersonIcon from '@material-ui/icons/Person';
import {
  Entity,
  parseEntityRef,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  CatalogAutocomplete,
  DefaultEntityFilters,
  entityPresentationApiRef,
  entityPresentationSnapshot,
  EntityFilter,
  EntityOwnerFilter,
  getEntityRelations,
  useEntityList,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import {
  compare,
  includesInvariantCase,
} from '@appfolio/backstage-plugin-utils-common';
import { getDefaultGroupRef } from '@appfolio/backstage-plugin-catalog-common';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const DEFAULT_GROUP_REF = stringifyEntityRef(getDefaultGroupRef());

const isUnownedOwnerFilter = (owners: EntityOwnerFilter | undefined): boolean =>
  owners?.values.length === 1 && owners.values[0] === DEFAULT_GROUP_REF;

class EntityOwnedByUserFilter implements EntityFilter {
  public filterEntity(entity: Entity): boolean {
    return (
      getEntityRelations(entity, RELATION_OWNED_BY, { kind: 'user' }).length > 0
    );
  }

  public toQueryValue(): string {
    return 'true';
  }
}

const FACET = 'relations.ownedBy';

const useStyles = makeStyles(theme => ({
  checkboxLabel: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

const OwnerOptionLabel = (props: { ownerRef: string }) => {
  const { primaryTitle: title } = useEntityPresentation(props.ownerRef);
  const isGroup = compare(parseEntityRef(props.ownerRef).kind, 'group');
  return (
    <Tooltip title={title}>
      <Box display="flex" alignItems="center">
        {isGroup ? (
          <GroupIcon fontSize="small" />
        ) : (
          <PersonIcon fontSize="small" />
        )}
        &nbsp;
        <Typography noWrap>{title}</Typography>
      </Box>
    </Tooltip>
  );
};

export const EntityOwnerPicker = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const entityPresentationApi = useApi(entityPresentationApiRef);
  const { filters, queryParameters, updateFilters } = useEntityList<
    DefaultEntityFilters & { ownedByUser?: EntityOwnedByUserFilter }
  >();

  const queryParamOwners = useMemo(
    () =>
      [queryParameters.owners]
        .flat()
        .filter((ref): ref is string => Boolean(ref)),
    [queryParameters.owners],
  );
  useEffect(() => {
    if (queryParamOwners.length) {
      updateFilters({ owners: new EntityOwnerFilter(queryParamOwners) });
    }
  }, [queryParamOwners, updateFilters]);

  const queryParamOwnedByUser = queryParameters.ownedByUser;
  useEffect(() => {
    if (queryParamOwnedByUser) {
      updateFilters({ ownedByUser: new EntityOwnedByUserFilter() });
    }
  }, [queryParamOwnedByUser, updateFilters]);

  const { value: ownerRefs = [] } = useAsync(async (): Promise<string[]> => {
    const response = await catalogApi.getEntityFacets({ facets: [FACET] });
    return response.facets[FACET].map(({ value }) => value);
  }, [catalogApi]);

  const onlyOwnedByUser = Boolean(filters.ownedByUser);
  const onlyUnowned = isUnownedOwnerFilter(filters.owners);
  const options = useMemo(
    () =>
      onlyOwnedByUser
        ? ownerRefs.filter(ref => compare(parseEntityRef(ref).kind, 'user'))
        : ownerRefs,
    [ownerRefs, onlyOwnedByUser],
  );

  const selectedRefs = filters.owners?.values ?? [];

  if (includesInvariantCase(['user', 'group'], filters.kind?.value ?? '')) {
    return null;
  }

  const onOwnedByUserChange = (_: unknown, checked: boolean) => {
    if (!checked) {
      updateFilters({ ownedByUser: undefined });
      return;
    }
    const userOnlyRefs = selectedRefs.filter(ref =>
      compare(parseEntityRef(ref).kind, 'user'),
    );
    updateFilters({
      ownedByUser: new EntityOwnedByUserFilter(),
      owners: userOnlyRefs.length
        ? new EntityOwnerFilter(userOnlyRefs)
        : undefined,
    });
  };

  const onUnownedChange = (_: unknown, checked: boolean) => {
    updateFilters(
      checked
        ? {
            owners: new EntityOwnerFilter([DEFAULT_GROUP_REF]),
            ownedByUser: undefined,
          }
        : { owners: undefined },
    );
  };

  const onOwnersChange = (_: unknown, refs: string[]) => {
    updateFilters({
      owners: refs.length ? new EntityOwnerFilter(refs) : undefined,
    });
  };

  return (
    <Box pb={1} pt={1}>
      <Box display="flex" flexDirection="column" mb={2}>
        <FormControlLabel
          classes={{ label: classes.checkboxLabel }}
          control={
            <Checkbox checked={onlyUnowned} onChange={onUnownedChange} />
          }
          label="Filter to unowned"
        />
        <FormControlLabel
          classes={{ label: classes.checkboxLabel }}
          control={
            <Checkbox
              checked={onlyOwnedByUser}
              onChange={onOwnedByUserChange}
            />
          }
          label="Filter to individually owned"
        />
      </Box>
      <CatalogAutocomplete<string, true>
        label="Owner"
        name="owner-picker"
        multiple
        disableCloseOnSelect
        options={options}
        value={selectedRefs}
        getOptionLabel={ref =>
          entityPresentationSnapshot(ref, undefined, entityPresentationApi)
            .primaryTitle
        }
        onChange={onOwnersChange}
        renderOption={(ref, { selected }) => (
          <FormControlLabel
            control={
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected}
              />
            }
            onClick={event => event.preventDefault()}
            label={<OwnerOptionLabel ownerRef={ref} />}
          />
        )}
      />
    </Box>
  );
};
